import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/react-hooks";
import {
  CREATE_OR_UPDATE_TAX_VOUCHER_SEQUENCES,
  GET_CONSULTORY_EXPIRED_UNUSED_TAX_VOUCHERS,
  GET_CONSULTORY_ISSUED_TAX_VOUCHERS,
  GET_TYPE_TAX_VOUCHERS_SEQUENCES,
} from "./queries";
import { useToasts } from "react-toast-notifications";
import moment from "moment";

const INITIAL_STATE = {
  activeTab: 1,
  isOpen: false,
  isLoading: false,
  data: {},
  partialDataCopy: {
    endSeq: 0,
  },
  tabIssued: {
    data: [],
  },
  tabDefeated: {
    data: [],
  },
};

export default function useActions({
  taxVoucherId,
  dataDoctor,
  isOpen,
  sequenceLength,
}) {
  const [state, setState] = useState(INITIAL_STATE);
  const [actionsSaveOrUpdate] = useMutation(
    CREATE_OR_UPDATE_TAX_VOUCHER_SEQUENCES,
  );

  const { addToast } = useToasts();

  const {
    data: { getTypeTaxVoucherSequences } = {},
    loading: loadingTypeTaxVoucherSequences,
    refetch: refetchTypeTaxVouchers,
  } = useQuery(GET_TYPE_TAX_VOUCHERS_SEQUENCES, {
    skip: !isOpen,
    fetchPolicy: "network-only",
    variables: {
      where: {
        typeTaxVoucherId: taxVoucherId,
        pharmacyProviderId: dataDoctor?.centerId,
      },
      relations: ["taxVoucherSequencesHistory"],
    },
  });

  const {
    data: { getConsultoryIssuedTaxVouchers = [] } = {},
    loading: loadingIssuedTaxVoucherItems,
  } = useQuery(GET_CONSULTORY_ISSUED_TAX_VOUCHERS, {
    skip: !isOpen || !taxVoucherId || !dataDoctor?.centerId,
    fetchPolicy: "network-only",
    variables: {
      pharmacyProviderId: dataDoctor?.centerId,
      typeTaxVoucherId: taxVoucherId,
    },
  });

  const {
    data: { getConsultoryExpiredUnusedTaxVouchers = [] } = {},
    loading: loadingExpiredTaxVoucherItems,
  } = useQuery(GET_CONSULTORY_EXPIRED_UNUSED_TAX_VOUCHERS, {
    skip: !isOpen || !taxVoucherId || !dataDoctor?.centerId,
    fetchPolicy: "network-only",
    variables: {
      pharmacyProviderId: dataDoctor?.centerId,
      typeTaxVoucherId: taxVoucherId,
    },
  });

  const loadingTypeTaxVouchers =
    loadingTypeTaxVoucherSequences ||
    loadingIssuedTaxVoucherItems ||
    loadingExpiredTaxVoucherItems;

  const onChange = useCallback(
    (key, value) => {
      setState((prevState) => ({
        ...prevState,
        [key]: value,
      }));
    },
    [setState],
  );

  const handleValidation = useCallback(() => {
    const { data, partialDataCopy } = state;

    // Validar que todos los campos requeridos estén completos
    if (!data?.endSeq || !data?.expirationDate || !data?.sequenceAlert) {
      throw new Error("Debe completar todos los campos requeridos.");
    }

    // Validar que la secuencia final no sea menor a la anterior
    if (parseInt(data?.endSeq || 0) <= partialDataCopy.endSeq) {
      throw new Error(
        "El final de secuencia no puede ser menor o igual a la final de secuencia anterior.",
      );
    }

    if (moment().format("YYYY-MM-DD") >= data?.expirationDate) {
      throw new Error(
        "La fecha de expiración debe ser mayor a la fecha actual.",
      );
    }
  }, [state]);

  const onActionSaveOrUpdate = useCallback(async () => {
    try {
      onChange("isLoading", true);

      handleValidation();

      const { data, partialDataCopy } = state;

      const available =
        (data?.available || 0) +
        (parseInt(data?.endSeq || 0) - partialDataCopy.endSeq);

      const taxVoucherSequencesHistory = {
        pharmacyProviderId: dataDoctor?.centerId,
        startSeq: String(partialDataCopy.endSeq + 1).padStart(
          sequenceLength,
          "0",
        ),
        endSeq: data?.endSeq || undefined,
        expirationDate: data?.expirationDate,
        available: (parseInt(data?.endSeq || 0) - partialDataCopy.endSeq)
      };

      await actionsSaveOrUpdate({
        variables: {
          data: {
            id: data?.id || undefined,
            typeTaxVoucherId: taxVoucherId,
            pharmacyProviderId: dataDoctor?.centerId,
            available,
            last: data?.last || undefined,
            lastSeq: data?.lastSeq || undefined,
            endSeq: data?.endSeq || undefined,
            sequenceAlert: parseInt(data?.sequenceAlert) || undefined,
            frequencySeqAlert: parseInt(data?.frequencySeqAlert) || undefined,
            expirationDate: data?.expirationDate || undefined,
            taxVoucherSequencesHistory: [taxVoucherSequencesHistory],
          },
        },
      });
      await refetchTypeTaxVouchers();
 
      addToast("Secuencia de comprobante fiscal agregado con éxito.", {
        appearance: "success",
        autoDismiss: true,
      });
    } catch (error) {
      addToast(error.message, { appearance: "error", autoDismiss: true });
    } finally {
      onChange("isLoading", false);
    }
  }, [
    onChange,
    handleValidation,
    state,
    dataDoctor?.centerId,
    actionsSaveOrUpdate,
    taxVoucherId,
    refetchTypeTaxVouchers,
    addToast,
    sequenceLength,
  ]);

  const onChangeData = useCallback(
    ({ target: { value, name } }) => {
      setState((prevState) => ({
        ...prevState,
        data: {
          ...prevState.data,
          [name]: value,
        },
      }));
    },
    [setState],
  );

  const restoreState = useCallback(() => {
    setState(INITIAL_STATE);
  }, [setState]);

  useEffect(() => {
    if (!loadingTypeTaxVouchers && getTypeTaxVoucherSequences) {
      const taxVoucherSequences = getTypeTaxVoucherSequences[0] || {};

      const partialDataCopy = {
        endSeq: parseInt(taxVoucherSequences?.endSeq) || 0,
      };

      const issuedData = (getConsultoryIssuedTaxVouchers || []).map((item) => ({
        name: item?.name || "",
        date: item?.date || null,
        receiver: item?.receiver || "",
        rnc: item?.rnc || "",
        amount: item?.amount || 0,
        expirationDate: item?.expirationDate || null,
      }));

      const defeatedData = (getConsultoryExpiredUnusedTaxVouchers || []).map(
        (item) => ({
          name: item?.name || "",
          date: null,
          receiver: "",
          rnc: "",
          amount: 0,
          expirationDate: item?.expirationDate || null,
        }),
      );

      setState((prevState) => ({
        ...prevState,
        data: taxVoucherSequences || {},
        partialDataCopy,
        tabIssued: {
          data: issuedData,
        },
        tabDefeated: {
          data: defeatedData,
        },
      }));
    }
  }, [
    getTypeTaxVoucherSequences,
    loadingTypeTaxVouchers,
    getConsultoryIssuedTaxVouchers,
    getConsultoryExpiredUnusedTaxVouchers,
  ]);

  return [
    { state },
    {
      onChange,
      restoreState,
      loadingTypeTaxVouchers,
      onActionSaveOrUpdate,
      onChangeData,
    },
  ];
}
